import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { getTokens } from '@/config/apiServer/scanner'
import type { ScannerTokenItem } from '@/config/apiServer/scanner'

const defaultIcon = require('../assets/img/common/baseCoin.png') as string

const SEARCH_FETCH_SIZE = 999

export interface TokenTableRow extends ScannerTokenItem {
  index: number
  value: string
  icon: string
  introduce?: string
}

export interface AutocompleteTokenItem {
  value: string
  symbol: string
  icon: string
  address: string
  name?: string
}

export default function useTokenList(): {
  coinSearch: ReturnType<typeof ref<string>>
  pages: { currentPage: number; pageSize: number; total: number }
  Tokens: ReturnType<typeof ref<TokenTableRow[]>>
  loading: ReturnType<typeof ref<boolean>>
  searchMode: ReturnType<typeof ref<boolean>>
  searchTableLoading: ReturnType<typeof ref<boolean>>
  loadTokenList: () => Promise<void>
  searchToken: (token: string) => Promise<void>
  querySearch: (
    queryString: string,
    cb: (arg: AutocompleteTokenItem[]) => void
  ) => Promise<void>
  pageChange: (page: number) => void
  sizeChange: (size: number) => void
  goTokenDetail: (row: TokenTableRow | ScannerTokenItem) => void
  selectSearchSuggestion: (item: AutocompleteTokenItem) => void
} {
  const router = useRouter()
  const coinSearch = ref('')
  const pages = reactive({
    currentPage: 1,
    pageSize: 10,
    total: 0,
  })
  const Tokens = ref<TokenTableRow[]>([])
  const TokensList = ref<TokenTableRow[]>([])
  const loading = ref(false)
  const searchMode = ref(false)
  const searchTableLoading = ref(false)

  function mapTokenRow(item: ScannerTokenItem, index: number): TokenTableRow {
    return {
      ...item,
      index: index + 1,
      value: item.symbol,
      icon: defaultIcon,
      introduce: (item.name as string) || '',
    }
  }

  function goTokenDetail(row: TokenTableRow | ScannerTokenItem): void {
    if (!row || !row.address) return
    router.push({
      path: '/tokenDetail',
      query: { tokenAdr: String(row.address) },
    })
  }

  function selectSearchSuggestion(item: AutocompleteTokenItem): void {
    coinSearch.value = item.value || item.symbol || ''
    if (item.address) {
      goTokenDetail(item as TokenTableRow)
    }
  }

  async function loadTokenList(): Promise<void> {
    loading.value = true
    try {
      const response = await getTokens(
        pages.currentPage,
        pages.pageSize
      )
      if (response.code === 0 && response.data) {
        const { tokens, total } = response.data
        const list = tokens || []
        const startIndex = (pages.currentPage - 1) * pages.pageSize
        TokensList.value = list.map((item, i) =>
          mapTokenRow(item, startIndex + i)
        )
        pages.total = total
        Tokens.value = TokensList.value
        searchMode.value = false
      } else {
        Tokens.value = []
        TokensList.value = []
        pages.total = 0
        searchMode.value = false
      }
    } catch {
      Tokens.value = []
      TokensList.value = []
      pages.total = 0
      searchMode.value = false
    } finally {
      loading.value = false
    }
  }

  async function searchToken(token: string): Promise<void> {
    const q = (token || '').trim()
    if (!q) {
      if (searchMode.value) {
        searchMode.value = false
        await loadTokenList()
      } else {
        ElMessage.info('请输入搜索关键词')
      }
      return
    }
    searchTableLoading.value = true
    try {
      // 0x开头走合约地址查询，否则走symbol模糊搜索
      const isAddress = q.startsWith('0x')
      const res = await getTokens(
        1,
        SEARCH_FETCH_SIZE,
        isAddress ? '' : q,
        isAddress ? q : ''
      )
      if (res.code === 0 && res.data) {
        const tokens = res.data.tokens || []
        searchMode.value = true
        Tokens.value = tokens.map((item, i) => mapTokenRow(item, i))
        if (!tokens.length) {
          ElMessage.warning('没有此Token')
        }
      } else {
        searchMode.value = true
        Tokens.value = []
        ElMessage.warning('没有此Token')
      }
    } catch {
      searchMode.value = true
      Tokens.value = []
      ElMessage.error('搜索失败，请稍后重试')
    } finally {
      searchTableLoading.value = false
    }
  }

  async function querySearch(
    queryString: string,
    cb: (arg: AutocompleteTokenItem[]) => void
  ): Promise<void> {
    const q = (queryString || '').trim()
    if (!q) {
      cb([])
      return
    }
    try {
      // 0x开头走合约地址查询，否则走symbol模糊搜索
      const isAddress = q.startsWith('0x')
      const res = await getTokens(
        1,
        20,
        isAddress ? '' : q,
        isAddress ? q : ''
      )
      if (res.code === 0 && res.data && res.data.tokens) {
        const results: AutocompleteTokenItem[] = (res.data.tokens || []).map(
          (item) => ({
            value: item.symbol,
            icon: defaultIcon,
            symbol: item.symbol,
            name: item.name as string | undefined,
            address: item.address,
          })
        )
        cb(results)
      } else {
        cb([])
      }
    } catch {
      cb([])
    }
  }

  function pageChange(page: number): void {
    pages.currentPage = page
    loadTokenList()
  }

  function sizeChange(size: number): void {
    pages.currentPage = 1
    pages.pageSize = size
    loadTokenList()
  }

  onMounted(() => {
    loadTokenList()
  })

  return {
    coinSearch,
    pages,
    Tokens,
    loading,
    searchMode,
    searchTableLoading,
    loadTokenList,
    searchToken,
    querySearch,
    pageChange,
    sizeChange,
    goTokenDetail,
    selectSearchSuggestion,
  }
}
